import React, { useEffect, useState, useRef } from 'react';
import { STATS_DATA } from '../constants';
import gsap from 'gsap';

const useCountUp = (end: number, duration: number = 2000, start: boolean = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime: number | null = null;
    let animationFrameId: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = currentTime - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function (easeOutExpo)
      const easeOut = (x: number): number => {
        return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
      }

      setCount(Math.floor(easeOut(percentage) * end));

      if (progress < duration) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [end, duration, start]);

  return count;
};

const StatItemRenderer: React.FC<{ label: string; value: string; index: number }> = ({ label, value, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Parse number and suffix from value string (e.g., "50+ ARENA STAGES" -> 50, "+ ARENA STAGES")
  const numberMatch = value.match(/^(\d+)(.*)$/);
  const isNumber = !!numberMatch;
  const targetNumber = isNumber ? parseInt(numberMatch[1], 10) : 0;
  const suffix = isNumber ? numberMatch[2] : value;
  
  const currentNumber = useCountUp(targetNumber, 2000, isVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div 
      ref={ref}
      className={`
        stat-item py-4 px-4 md:py-8 flex items-center justify-between md:flex-col md:justify-center md:px-8 opacity-0
        ${index !== 0 ? 'border-t md:border-t-0 border-slate-200 dark:border-slate-800' : ''}
        ${index !== 0 ? 'md:border-l' : ''}
      `}
    >
      <span className="font-mono text-xs font-bold uppercase text-slate-700 dark:text-slate-300 md:mb-2">
        {label}
      </span>
      <span className="font-bold text-sm md:text-xl lg:text-2xl text-slate-900 dark:text-white font-display tabular-nums">
        {isNumber ? (
            <>
                {currentNumber}{suffix}
            </>
        ) : (
            value
        )}
      </span>
    </div>
  );
};

const Stats: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            gsap.fromTo('.stat-item', 
              { y: 20, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: "power3.out" }
            );
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }
  }, []);

  return (
    <section ref={sectionRef} className="px-6 mb-16 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-b border-slate-200 dark:border-slate-800">
        {STATS_DATA.map((stat, index) => (
          <StatItemRenderer 
            key={stat.label} 
            label={stat.label} 
            value={stat.value} 
            index={index} 
          />
        ))}
      </div>
    </section>
  );
};

export default Stats;